import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";

export interface PriorityListRef {
  syncVariableChange: (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allResponses1: Record<string, any>,
  ) => void;
}

interface PriorityListComponentProps {
  label: string;
  onChange: (val: string[], displayAs?: string) => void;
  value?: string[];
  linkedVariable?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parsePriorityListChoices: (allResponses1?: Record<string, any>) => string[];
}

const PriorityListComponent = forwardRef<
  PriorityListRef,
  PriorityListComponentProps
>(
  (
    {
      label,
      onChange,
      value = [],
      linkedVariable = [],
      parsePriorityListChoices,
    },
    ref,
  ) => {
    const processValueDisplayAs = (value: string[]) => {
      return value.map((item, index) => `${index + 1}. ${item}`).join("\n");
    };
    // Expose function to parent component
    useImperativeHandle(
      ref,
      () => ({
        // Function to update the recapState value of priority list
        syncVariableChange: (
          variable: string,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          allResponses1: Record<string, any>,
        ) => {
          // If only the variable that is changed is linked to the priority list, update the value of recap state
          if (linkedVariable.includes(variable)) {
            const choices = parsePriorityListChoices(allResponses1);
            const newOrderedItems = [...value];
            choices.forEach((choice) => {
              if (!newOrderedItems.includes(choice)) {
                newOrderedItems.push(choice);
              }
            });
            const filteredItems = newOrderedItems.filter((item) =>
              choices.includes(item),
            );
            onChange(filteredItems, processValueDisplayAs(filteredItems));
          }
        },
      }),
      [linkedVariable, value, onChange, parsePriorityListChoices],
    );

    useEffect(() => {
      setTimeout(() => {
        onChange(parsePriorityListChoices(), processValueDisplayAs(value));
      }, 200);
    }, []);

    const handleDragEnd = (result) => {
      if (!result.destination) return;
      const items = Array.from(value); // Use orderedItems instead of choices
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      onChange(items, processValueDisplayAs(items));
    };

    return (
      <div className="mt-4 px-4">
        <div
          className="mb-2 font-bold"
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {label}
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="priority-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-2"
              >
                {value.map((choice, index) => (
                  <Draggable key={choice} draggableId={choice} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`flex items-center p-3 rounded-md shadow-sm border border-gray-200 cursor-grab hover:bg-electrum-50 transition-colors ${
                          snapshot.isDragging ? "bg-electrum-50" : "bg-white"
                        }`}
                      >
                        <div className="mr-3">
                          <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        <span className="flex-1">{choice}</span>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  },
);

PriorityListComponent.displayName = "PriorityListComponent";

export default PriorityListComponent;
